/**
 * Componente de Botón de Acción Estandarizado
 * 
 * Propósito: Unificar todos los botones del portal con un diseño consistente
 * Uso: Reemplaza botones de Bootstrap dispersos por este componente
 * 
 * Ejemplos de uso:
 * 
 * <ActionButton 
 *   variant="view" 
 *   onClick={handleView}
 *   tooltip="Ver detalles"
 * />
 * 
 * <ActionButton 
 *   variant="download" 
 *   onClick={handleDownload}
 *   disabled={!canDownload}
 * />
 */

import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { 
  FaEye, 
  FaDownload, 
  FaEdit, 
  FaTrash, 
  FaCheck, 
  FaTimes,
  FaExclamationTriangle,
  FaInfoCircle,
  FaPlus,
  FaSave,
  FaUndo,
  FaFileDownload,
  FaCheckCircle
} from 'react-icons/fa';
import './ActionButton.css';

/**
 * Configuración de variantes de botones
 * Cada variante tiene: icono, color de fondo, color hover, tooltip por defecto
 */
const BUTTON_VARIANTS = {
  // Acciones de visualización
  view: {
    icon: FaEye,
    className: 'action-btn-view',
    defaultTooltip: 'Ver',
    ariaLabel: 'Ver detalles'
  },
  info: {
    icon: FaInfoCircle,
    className: 'action-btn-info',
    defaultTooltip: 'Información',
    ariaLabel: 'Ver información'
  },
  
  // Acciones de descarga
  download: {
    icon: FaDownload,
    className: 'action-btn-download',
    defaultTooltip: 'Descargar',
    ariaLabel: 'Descargar archivo'
  },
  downloadFile: {
    icon: FaFileDownload,
    className: 'action-btn-download',
    defaultTooltip: 'Descargar',
    ariaLabel: 'Descargar archivo'
  },
  
  // Acciones de edición
  edit: {
    icon: FaEdit,
    className: 'action-btn-edit',
    defaultTooltip: 'Editar',
    ariaLabel: 'Editar'
  },
  
  // Acciones de eliminación
  delete: {
    icon: FaTrash,
    className: 'action-btn-delete',
    defaultTooltip: 'Eliminar',
    ariaLabel: 'Eliminar'
  },
  
  // Acciones de validación
  validate: {
    icon: FaCheck,
    className: 'action-btn-validate',
    defaultTooltip: 'Validar',
    ariaLabel: 'Validar'
  },
  checkCircle: {
    icon: FaCheckCircle,
    className: 'action-btn-validate',
    defaultTooltip: 'Marcar como validado',
    ariaLabel: 'Validar'
  },
  
  // Acciones de advertencia/reclamo
  warning: {
    icon: FaExclamationTriangle,
    className: 'action-btn-warning',
    defaultTooltip: 'Advertencia',
    ariaLabel: 'Ver advertencia'
  },
  
  // Acciones de rechazo
  reject: {
    icon: FaTimes,
    className: 'action-btn-reject',
    defaultTooltip: 'Rechazar',
    ariaLabel: 'Rechazar'
  },
  
  // Acciones de creación
  add: {
    icon: FaPlus,
    className: 'action-btn-add',
    defaultTooltip: 'Agregar',
    ariaLabel: 'Agregar nuevo'
  },
  
  // Acciones de guardado
  save: {
    icon: FaSave,
    className: 'action-btn-save',
    defaultTooltip: 'Guardar',
    ariaLabel: 'Guardar cambios'
  },
  
  // Acciones de cancelación
  cancel: {
    icon: FaUndo,
    className: 'action-btn-cancel',
    defaultTooltip: 'Cancelar',
    ariaLabel: 'Cancelar'
  }
};

const ActionButton = ({ 
  variant = 'view',
  onClick,
  disabled = false,
  tooltip,
  size = 'md',
  className = '',
  children,
  loading = false,
  ...props 
}) => {
  const config = BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.view;
  const Icon = config.icon;
  const tooltipText = tooltip || config.defaultTooltip;

  const buttonClasses = [
    'action-btn',
    config.className,
    `action-btn-${size}`,
    disabled && 'action-btn-disabled',
    loading && 'action-btn-loading',
    className
  ].filter(Boolean).join(' ');

  const buttonContent = (
    <button
      className={buttonClasses}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      aria-label={config.ariaLabel}
      {...props}
    >
      {loading ? (
        <span className="action-btn-spinner" />
      ) : (
        <>
          <Icon className="action-btn-icon" />
          {children && <span className="action-btn-text">{children}</span>}
        </>
      )}
    </button>
  );

  // Si tiene tooltip, envolver en OverlayTrigger
  if (tooltipText && !disabled) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={`tooltip-${variant}`}>{tooltipText}</Tooltip>}
      >
        {buttonContent}
      </OverlayTrigger>
    );
  }

  return buttonContent;
};

/**
 * Grupo de botones de acción
 * Útil para agrupar múltiples botones en una fila con espaciado consistente
 */
export const ActionButtonGroup = ({ children, className = '', align = 'start' }) => {
  return (
    <div className={`action-btn-group action-btn-group-${align} ${className}`}>
      {children}
    </div>
  );
};

export default ActionButton;
